// components/MultipleSelectDropdown.jsx
import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

export default function MultipleSelectDropdown({
  label,
  options = [],

  // Direkt-läge (filter)
  selected,                 // array av values
  onChange,                 // (updatedValues) => void

  // Bekräfta-läge (Add)
  selectedOptions,          // array av values (visning ovanför)
  setSelectedOptions,       // (vals) => void
  onSelect,                 // (vals) => void
  withAddButton = false,

  searchable = false,
  disabled = false,
  className = "",
  placeholder = "Select...",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState([]); // används endast i bekräfta-läge
  const ref = useRef(null);

  // normalisera options => {label, value:string}
  const norm = options.map((o) =>
    typeof o === "string" ? { label: o, value: o } : { ...o, value: String(o.value) }
  );

  // avgör läge
  const confirmMode = Boolean(withAddButton && onSelect);
  const instantMode = !confirmMode;

  // visa-värden i knappen
  const shownValues = confirmMode
    ? draft
    : Array.isArray(selected)
      ? selected.map(String)
      : [];

  const shownLabels = norm
    .filter((o) => shownValues.includes(o.value))
    .map((o) => o.label);

  // synka in prop -> intern draft när vi kör bekräfta-läge
  useEffect(() => {
    if (confirmMode) {
      const base = Array.isArray(selectedOptions) ? selectedOptions.map(String) : [];
      setDraft(base);
    }
  }, [confirmMode, selectedOptions]);

  // utanför-klick stänger
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = searchable
    ? norm.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()))
    : norm;

  const toggleValue = (value) => {
    if (instantMode) {
      // uppdatera direkt via onChange
      const base = Array.isArray(selected) ? selected.map(String) : [];
      const updated = base.includes(value)
        ? base.filter((v) => v !== value)
        : [...base, value];
      onChange && onChange(updated);
    } else {
      // bekräfta-läge: jobba i draft
      setDraft((prev) =>
        prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
      );
    }
  };

  const toggleSelectAll = () => {
    const allValues = norm.map((o) => o.value);
    if (instantMode) {
      const base = Array.isArray(selected) ? selected.map(String) : [];
      const allSelected = allValues.every((v) => base.includes(v));
      onChange && onChange(allSelected ? [] : allValues);
    } else {
      const allSelected = allValues.every((v) => draft.includes(v));
      setDraft(allSelected ? [] : allValues);
    }
  };

  const isAllSelected = () => {
    const allValues = norm.map((o) => o.value);
    if (instantMode) {
      const base = Array.isArray(selected) ? selected.map(String) : [];
      return allValues.length > 0 && allValues.every((v) => base.includes(v));
    } else {
      return allValues.length > 0 && allValues.every((v) => draft.includes(v));
    }
  };

  const handleAdd = () => {
    // skicka upp den färska draften
    onSelect && onSelect(draft);
    // rensa visning om du vill
    setDraft([]);
    setSelectedOptions && setSelectedOptions([]);
    setQ("");
    setOpen(false);
  };

  return (
    <div ref={ref} className={`flex flex-col w-full ${className}`}>
      {label && <label className="block text-sm font-bold text-gray-800 mb-2">{label}</label>}
      <div className="relative">
        {!open || !searchable ? (
          <button
            disabled={disabled}
            onClick={() => setOpen((p) => !p)}
            className={`w-full text-left px-3 py-2 border rounded-md ${
              disabled ? "bg-gray-100 cursor-not-allowed opacity-60" : "input-default"
            }`}
          >
            <div className="h-[1.5rem] overflow-hidden">
              {shownLabels.length ? (
                isAllSelected() ? "All" : shownLabels.join(", ")
              ) : (
                <span className="text-gray-400">{placeholder}</span>
              )}
            </div>
            <FontAwesomeIcon
              icon={faChevronDown}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
            />
          </button>
        ) : (
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search..."
            className="input-default w-full px-2 py-1"
          />
        )}

        {open && (
          <div className="absolute mt-1 w-full bg-white border rounded-md shadow-lg z-10">
            <div className="max-h-64 overflow-y-auto">
              {norm.length > 0 && (
                <label className="flex items-center py-2 px-3 hover:bg-blue-50 cursor-pointer border-b bg-gray-50 font-semibold">
                  <input
                    type="checkbox"
                    className="custom-checkbox-input hidden"
                    checked={isAllSelected()}
                    onChange={toggleSelectAll}
                  />
                  <div className="custom-checkbox mr-2">
                    <svg
                      className="custom-checkbox-icon"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  Select All
                </label>
              )}
              {filtered.length ? (
                filtered.map((o) => {
                  const checked = confirmMode
                    ? draft.includes(o.value)
                    : Array.isArray(selected) && selected.map(String).includes(o.value);
                  return (
                    <label
                      key={o.value}
                      className="flex items-center py-2 px-3 hover:bg-blue-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        className="custom-checkbox-input hidden"
                        checked={checked}
                        onChange={() => toggleValue(o.value)}
                      />
                      <div className="custom-checkbox mr-2">
                        <svg
                          className="custom-checkbox-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      {o.label}
                    </label>
                  );
                })
              ) : (
                <p className="text-gray-500 px-3 py-2">No options found</p>
              )}
            </div>

            {confirmMode && (
              <div className="p-2 border-t">
                <button
                  onClick={handleAdd}
                  className="w-full rounded-md px-3 py-2 bg-primary-300 text-primary-700 font-semibold hover:bg-primary-400"
                >
                  Add selected
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
